<template>
  <v-container fluid>
    <v-card>
      <v-toolbar
        color="indigo"
        dark>

        <v-toolbar-title>
          <v-icon left>{{ filtersIcon }}</v-icon>
          {{ $vuetify.lang.t('$vuetify.charts.filters.title') }}
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon @click="showContent = !showContent">
          <v-icon>{{ showContent ? upIcon : downIcon }}</v-icon>
        </v-btn>
      </v-toolbar>

      <v-progress-linear :active="isLoading" indeterminate absolute bottom>
      </v-progress-linear>

      <v-expand-transition>
        <div v-show="showContent">
          <v-card-text>
            <v-row>
              <v-col cols="12" md="4">
                <datetime-chart-filter :start-date="`${date.start.date}`"
                                       :finish-date="`${date.finish.date}`"
                                       :header="$vuetify.lang.t('$vuetify.charts.filters.date.title')"
                                       @datetime-changed="setDatetime"></datetime-chart-filter>
              </v-col>
              <v-col cols="12" md="8">
                <v-card>
                  <critical-dataset-form :dataset-entry="filterDataset" :is-filter="true"
                                         class="pa-3"></critical-dataset-form>
                </v-card>
              </v-col>
            </v-row>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="success" tile class="mx-2 mb-2" @click="applyFilterChanges">
              <v-icon left>{{ applyFiltersIcon }}</v-icon>
              {{ $vuetify.lang.t('$vuetify.shared.apply') }}
            </v-btn>
          </v-card-actions>
          <v-divider></v-divider>
        </div>
      </v-expand-transition>
    </v-card>

    <v-data-iterator
      :items="items"
      :footer-props="footerProps"
      :server-items-length="serverTotal"
      :options.sync="options"
    >
      <template v-slot:default="props">
        <v-row class="py-0" no-gutters justify="center">
          <v-col cols="12" md="4" v-for="entry in props.items" :key="entry.id_critical_event">
            <v-card flat height="100%">
              <v-card-title class="text-center justify-center py-0">
                <v-btn icon :href="`${siteUrl}?travel=${entry.travelId}&critical=${entry.event.idCriticalEvent}`"
                       target="_blank">
                  <v-icon>{{ linkIcon }}</v-icon>
                </v-btn>
                <!--                <span> {{ entry.event.dangerousStateLocalized }}</span>-->
                <v-card-subtitle class="text-center px-0">
                  {{ entry.event.formattedTimeEventStart }}
                </v-card-subtitle>
              </v-card-title>

              <v-card-text class="text-center">
                <v-row no-gutters align="stretch">
                  <v-col cols="12">
                    <dataset-chip-list :categories="entry.dataset.datasetCategories"
                                       :critical-event-id="entry.event.idCriticalEvent"></dataset-chip-list>
                  </v-col>
                  <v-col cols="12" class="fill-height">
                    <critical-video :event="entry.event" :user-id="entry.userId"
                                    :key="`video-${entry.event.datetime}`"></critical-video>
                  </v-col>
                </v-row>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>
    </v-data-iterator>
  </v-container>
</template>

<script>
import showContentMixin from '../../mixins/showContentMixin'
import { mdiFilterMenu, mdiFilterOutline, mdiLinkBoxVariantOutline } from '@mdi/js'
import backend from '../../backend'
import { CriticalEvent } from '../../models/map/CriticalEvent'
import { CriticalDatasetEntry } from '../../models/criticalDataset/CriticalDatasetEntry'
import { dangerousStateIcons } from '../../utils/icons'
import CriticalVideo from '../../components/map/criticalPopup/criticalVideo'
import DatasetChipList from '../../components/datasetViewer/datasetChipList'
import CriticalDatasetForm from '../../components/map/criticalDataset/CriticalDatasetForm'
import { defaultChartDatetimeState } from '../../utils/filters'
import DatetimeChartFilter from '../../components/charts/utils/datetimeChartFilter'

export default {
  name: 'DatasetViewer',
  components: { DatetimeChartFilter, CriticalDatasetForm, DatasetChipList, CriticalVideo },
  mixins: [showContentMixin(false)],
  data: () => ({
    items: [],
    serverTotal: 0,
    isLoading: false,
    footerProps: { 'items-per-page-options': [6, 12, 18, 24, 36] },
    options: {
      page: 1,
      itemsPerPage: 12
    },

    filterDataset: new CriticalDatasetEntry(),
    date: {
      start: defaultChartDatetimeState(),
      finish: defaultChartDatetimeState()
    },
    icons: dangerousStateIcons(),
    filtersIcon: mdiFilterMenu,
    applyFiltersIcon: mdiFilterOutline,
    linkIcon: mdiLinkBoxVariantOutline
  }),
  computed: {
    siteUrl () {
      return document.location.origin + '/panel/map'
    }
  },
  methods: {
    async applyFilterChanges () {
      // it's not a bug - after button hitting we just reset page number to 1 -> watch handler will call loadDataset() with updated filters, time range and page
      if (this.options.page !== 1) {
        this.options.page = 1
      } else {
        await this.loadDataset()
      }
    },
    async loadDataset () {
      this.isLoading = true
      try {
        const data = { ...this.options }
        data['date'] = this.date

        if (!this.filterDataset.isFilterEmpty()) {
          data['dataset'] = this.filterDataset.backendModel()
          data['dataset_filters'] = this.filterDataset.filterModel()
        }

        const response = await backend.fetchDatasetViewerData(data)

        const result = response.data

        this.items = result.items.map(item => {
          return {
            event: new CriticalEvent({ ...item }),
            travelId: item.id,
            userId: item.user_id,
            dataset: new CriticalDatasetEntry({ ...item.dataset })
          }
        })
        this.serverTotal = result.total

      } catch (e) {
        console.error(e)
      } finally {
        this.isLoading = false
      }
    },
    setDatetime (datetime) {
      this.date[datetime.type][datetime.mode] = datetime.value
    },
  },
  watch: {
    options: {
      async handler (value) {
        await this.loadDataset()
      },
      deep: true
    }
  }
}
</script>

<style scoped>

</style>
